import { z } from 'zod';
import type { CardType, TransactionDetails } from './payment.types';
import { TransactionDetailsSchema } from './schemas/payment.schemas';
import type { PlatformPaymentMethods } from './backend_config.types';
import { PlatformPaymentMethodsSchema } from './backend_config.types';

export const StateType = {
    INIT: 'init',
    LIST_DISPLAY: 'list-display',
    OBJECT_DISPLAY: 'object-display',
    PAYMENTS_HOME: 'payments-home',
    CARD_PAYMENT: 'card-payment',
    ACH_PAYMENT: 'ach-payment',
    TERMINAL_PAYMENT: 'terminal-payment',
    E_INVOICE: 'e-invoice',
    TRANSACTION_STATUS: 'transaction-status',
    RECENT_PAYMENTS: 'recent-payments',
    PAYMENTS_AUDIT_LOG: 'payments-audit-log',
    TRANSACTION_DETAILS: 'transaction-details',
    RECONCILE_PAYMENTS: 'reconcile-payments',
} as const;

export type StateType = (typeof StateType)[keyof typeof StateType];

export const DealertrackScreenId = {
    DEAL_BUSINESS_OFFICE: 'DEAL_BUSINESS_OFFICE',
    CASHIER_PARTS: 'CASHIER_PARTS',
    CASHIER_SERVICE: 'CASHIER_SERVICE',
    PARTS_INVOICE_TRANSACTION: 'PARTS_INVOICE_TRANSACTION',
    REPAIR_ORDER_DETAIL: 'REPAIR_ORDER_DETAIL',
    REPAIR_ORDER_TOTALS: 'REPAIR_ORDER_TOTALS',
    UNKNOWN_CONTEXT: 'UNKNOWN_CONTEXT',
} as const;

export type DealertrackScreenId = (typeof DealertrackScreenId)[keyof typeof DealertrackScreenId];

export interface ListDisplayAction {
    workflowId: string;
    label: string;
}

export interface ListDisplayData {
    items: Array<Record<string, unknown>>;
}

export interface ObjectDisplayData {
    title: string;
    [key: string]: unknown;
}

export interface PaymentsHomeData {}

export interface CardData {
    cardNumber: string;
    expiryDate: string;
    cvv: string;
    cardholderName: string;
    cardType: CardType | undefined;
}

export interface ACHData {
    routingNumber: string;
    accountNumber: string;
    accountType: 'checking' | 'savings';
    accountHolderName: string;
    bankName?: string;
}

export interface TerminalData {
    amount: string;
    description: string;
}

export interface PaymentState {
    type:
        | typeof StateType.PAYMENTS_HOME
        | typeof StateType.CARD_PAYMENT
        | typeof StateType.ACH_PAYMENT
        | typeof StateType.TERMINAL_PAYMENT;
    title: string;
    data?: CardData | ACHData | TerminalData;
}

export type PaymentType =
    | typeof StateType.CARD_PAYMENT
    | typeof StateType.ACH_PAYMENT
    | typeof StateType.TERMINAL_PAYMENT
    | typeof StateType.E_INVOICE;

export interface RecentPaymentsData {
    ipw_system_id: number;
    ipw_customer_id: string;
}

export interface PaymentsAuditLogData {
    ipw_system_id: number;
}

export interface TransactionDetailsData {
    payment_transaction_uuid: string;
    ipw_system_id: number;
    ipw_customer_id: string;
}

export interface ReconcilePaymentsData {}

export interface TransactionStatusData {
    paymentType: PaymentType;
    transactionDetails: TransactionDetails;
    accountId?: string | number;
    terminalDeviceUuid?: string;
    credentialUuid?: string;
    savedCardToken?: string;
    cardData?: {
        cardNumber: string;
        expiryDate: string;
        cvv: string;
        cardholderName: string;
    };
    achData?: {
        accountNumber: string;
        routingNumber: string;
        payeeName: string;
        accountType: 'C' | 'S';
        entryClass: 'Business' | 'Personal';
    };
    can_get_receipt?: boolean;
    missing_receipt_fields?: string[];
    receipt_generation_inflight?: boolean;
    receipt_file_path?: string;
}

interface BaseAppState {
    title: string;
    originalListState?: AppState;
    defaultAction?: ListDisplayAction;
    actions?: Array<ListDisplayAction>;
    paymentsEnabled?: boolean;
    reconciliationEnabled?: boolean;
    paymentMethods?: PlatformPaymentMethods;
    ipw_system_id?: number;
}

export type AppState =
    | (BaseAppState & { type: typeof StateType.INIT; data: null })
    | (BaseAppState & { type: typeof StateType.LIST_DISPLAY; data: ListDisplayData })
    | (BaseAppState & { type: typeof StateType.OBJECT_DISPLAY; data: ObjectDisplayData })
    | (BaseAppState & { type: typeof StateType.PAYMENTS_HOME; data: PaymentsHomeData })
    | (BaseAppState & { type: typeof StateType.CARD_PAYMENT; data: null })
    | (BaseAppState & { type: typeof StateType.ACH_PAYMENT; data: null })
    | (BaseAppState & { type: typeof StateType.TERMINAL_PAYMENT; data: null })
    | (BaseAppState & { type: typeof StateType.E_INVOICE; data: null })
    | (BaseAppState & { type: typeof StateType.TRANSACTION_STATUS; data: TransactionStatusData })
    | (BaseAppState & { type: typeof StateType.RECENT_PAYMENTS; data: RecentPaymentsData })
    | (BaseAppState & { type: typeof StateType.PAYMENTS_AUDIT_LOG; data: PaymentsAuditLogData })
    | (BaseAppState & {
          type: typeof StateType.TRANSACTION_DETAILS;
          data: TransactionDetailsData;
      })
    | (BaseAppState & {
          type: typeof StateType.RECONCILE_PAYMENTS;
          data: ReconcilePaymentsData;
      });

export interface SidebarConfig {
    enable_manual_transaction_details?: boolean;
}

const ListDisplayActionSchema: z.ZodType<ListDisplayAction> = z.object({
    workflowId: z.string(),
    label: z.string(),
});

const ListDisplayDataSchema: z.ZodType<ListDisplayData> = z.object({
    items: z.array(z.record(z.string(), z.unknown())),
});

const ObjectDisplayDataSchema: z.ZodType<ObjectDisplayData> = z
    .object({
        title: z.string(),
    })
    .passthrough();

const PaymentsHomeDataSchema: z.ZodType<PaymentsHomeData> = z.object({}).passthrough();

const RecentPaymentsDataSchema: z.ZodType<RecentPaymentsData> = z.object({
    ipw_system_id: z.number(),
    ipw_customer_id: z.string(),
});

const PaymentsAuditLogDataSchema: z.ZodType<PaymentsAuditLogData> = z.object({
    ipw_system_id: z.number(),
});

const TransactionDetailsDataSchema: z.ZodType<TransactionDetailsData> = z.object({
    payment_transaction_uuid: z.string().uuid(),
    ipw_system_id: z.number(),
    ipw_customer_id: z.string(),
});

const ReconcilePaymentsDataSchema: z.ZodType<ReconcilePaymentsData> = z.object({}).passthrough();

const TransactionStatusDataSchema: z.ZodType<TransactionStatusData> = z.object({
    paymentType: z.enum([
        StateType.CARD_PAYMENT,
        StateType.ACH_PAYMENT,
        StateType.TERMINAL_PAYMENT,
        StateType.E_INVOICE,
    ]),
    transactionDetails: TransactionDetailsSchema,
    accountId: z.union([z.string().min(1), z.number()]).optional(),
    terminalDeviceUuid: z.string().uuid().optional(),
    credentialUuid: z.string().uuid().optional(),
    savedCardToken: z.string().min(1).optional(),
    cardData: z
        .object({
            cardNumber: z.string(),
            expiryDate: z.string(),
            cvv: z.string(),
            cardholderName: z.string(),
        })
        .optional(),
    achData: z
        .object({
            accountNumber: z.string(),
            routingNumber: z.string(),
            payeeName: z.string(),
            accountType: z.enum(['C', 'S']),
            entryClass: z.enum(['Business', 'Personal']),
        })
        .optional(),
    can_get_receipt: z.boolean().optional(),
    missing_receipt_fields: z.array(z.string()).optional(),
    receipt_generation_inflight: z.boolean().optional(),
    receipt_file_path: z.string().optional(),
});

const BaseAppStateSchemaFields = {
    title: z.string(),
    originalListState: z.lazy(() => AppStateSchema).optional(),
    defaultAction: ListDisplayActionSchema.optional(),
    actions: z.array(ListDisplayActionSchema).optional(),
    paymentsEnabled: z.boolean().optional(),
    reconciliationEnabled: z.boolean().optional(),
    paymentMethods: PlatformPaymentMethodsSchema.optional(),
    ipw_system_id: z.number().optional(),
};

export const AppStateSchema: z.ZodType<AppState> = z.discriminatedUnion('type', [
    z.object({
        ...BaseAppStateSchemaFields,
        type: z.literal(StateType.INIT),
        data: z.null(),
    }),
    z.object({
        ...BaseAppStateSchemaFields,
        type: z.literal(StateType.LIST_DISPLAY),
        data: ListDisplayDataSchema,
    }),
    z.object({
        ...BaseAppStateSchemaFields,
        type: z.literal(StateType.OBJECT_DISPLAY),
        data: ObjectDisplayDataSchema,
    }),
    z.object({
        ...BaseAppStateSchemaFields,
        type: z.literal(StateType.PAYMENTS_HOME),
        data: PaymentsHomeDataSchema,
    }),
    z.object({
        ...BaseAppStateSchemaFields,
        type: z.literal(StateType.CARD_PAYMENT),
        data: z.null(),
    }),
    z.object({
        ...BaseAppStateSchemaFields,
        type: z.literal(StateType.ACH_PAYMENT),
        data: z.null(),
    }),
    z.object({
        ...BaseAppStateSchemaFields,
        type: z.literal(StateType.TERMINAL_PAYMENT),
        data: z.null(),
    }),
    z.object({
        ...BaseAppStateSchemaFields,
        type: z.literal(StateType.E_INVOICE),
        data: z.null(),
    }),
    z.object({
        ...BaseAppStateSchemaFields,
        type: z.literal(StateType.TRANSACTION_STATUS),
        data: TransactionStatusDataSchema,
    }),
    z.object({
        ...BaseAppStateSchemaFields,
        type: z.literal(StateType.RECENT_PAYMENTS),
        data: RecentPaymentsDataSchema,
    }),
    z.object({
        ...BaseAppStateSchemaFields,
        type: z.literal(StateType.PAYMENTS_AUDIT_LOG),
        data: PaymentsAuditLogDataSchema,
    }),
    z.object({
        ...BaseAppStateSchemaFields,
        type: z.literal(StateType.TRANSACTION_DETAILS),
        data: TransactionDetailsDataSchema,
    }),
    z.object({
        ...BaseAppStateSchemaFields,
        type: z.literal(StateType.RECONCILE_PAYMENTS),
        data: ReconcilePaymentsDataSchema,
    }),
]);
